import { supabase } from './supabase';

/**
 * ⭐⭐ **HOW A SERVER-SIDE WIPE REACHES A LOCAL-FIRST CLIENT** (owner, 2026-09-19).
 *
 * The console's "reset content" empties the account's rows. It cannot empty a
 * browser, and two kinds of state came straight back after one — found by the
 * owner testing it, not by a test:
 *
 *   * **Onboarding.** `onboardingStore.initialize()` merges its flag with a
 *     logical OR — *"cloud true wins (once onboarded anywhere, stay
 *     onboarded)"*. The cloud can turn it ON and never OFF, so the browser's
 *     persisted `true` outlived the deletion of its `user_preferences` row.
 *   * **The growth journey.** `journeyReachedSteps` rides the identity blob:
 *     persisted here, and upserted FROM local state into `user_identity`. The
 *     row was deleted and the next write put it back.
 *
 * Content DID reset, because those stores treat the cloud as authoritative when
 * they hydrate. So the bug is not in either store — it is that a wipe had no
 * way to say *forget what you know*. `user_profiles.content_reset_at` (migration
 * 0269) is that message, and it survives its own reset because that table is on
 * the KEEP list.
 */

/** Where this browser records the last stamp it has acted on, per user. */
export const SEEN_KEY = 'metanoia-content-reset-seen';

/**
 * ⚠️⚠️ **THE SESSION SURVIVES, EVERYTHING ELSE GOES.** Clearing the auth key
 * would sign the person out of an account that still exists, turning "your
 * content was reset" into "you have been locked out".
 *
 * ⚠️ A LIST of stores to clear would be wrong the day someone adds the
 * fifty-first: there are ~50 persisted keys and the local-first ones are not
 * marked. So this keeps two and drops the rest — including
 * `metanoia-cloud-fresh`, whose five-minute stamps would otherwise let a store
 * boot from its stale cache without reading the cloud at all.
 */
export const survivesReset = (key: string): boolean =>
  key === SEEN_KEY || key === 'metanoia-auth' || key.startsWith('sb-');

type Seen = Record<string, string>;

export function readSeen(store: Storage): Seen {
  try {
    const raw = store.getItem(SEEN_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Seen) : {};
  } catch {
    return {};
  }
}

/**
 * What to do about a stamp.
 *
 * ⚠️⚠️ **AN UNKNOWN STAMP CLEARS.** When this browser has no record for the
 * account it cannot know whether its local state predates the reset — and the
 * two mistakes are not equal. Clearing costs a rehydrate from a cloud that is
 * already authoritative; not clearing is the bug this whole file exists for.
 */
export function decideReset(serverStamp: string | null, seen: string | undefined): 'skip' | 'clear' {
  if (!serverStamp) return 'skip';
  return seen === serverStamp ? 'skip' : 'clear';
}

/** Drops every key but the ones that survive. Returns how many went. */
export function forgetLocalState(store: Storage): number {
  const doomed: string[] = [];
  for (let i = 0; i < store.length; i++) {
    const key = store.key(i);
    if (key && !survivesReset(key)) doomed.push(key);
  }
  for (const key of doomed) {
    try {
      store.removeItem(key);
    } catch {
      /* one stubborn key must not strand the rest */
    }
  }
  return doomed.length;
}

export function rememberSeen(store: Storage, userId: string, stamp: string): void {
  try {
    store.setItem(SEEN_KEY, JSON.stringify({ ...readSeen(store), [userId]: stamp }));
  } catch {
    /* ⚠️ Storage can be blocked entirely. Losing the record costs one extra
       clear on the next boot, which is harmless; throwing here would not be. */
  }
}

/**
 * Acts on the account's reset stamp. Returns true when the page is reloading,
 * so the caller stops — anything it does next is about to be thrown away.
 *
 * ⚠️⚠️ **FAILS OPEN, ALWAYS.** A missed reset is caught on the next boot; a boot
 * that hangs on this check is a broken app. Every failure path returns false.
 */
export async function honourContentReset(
  userId: string,
  deps: {
    store?: Storage;
    reload?: () => void;
    fetchStamp?: (id: string) => Promise<string | null>;
  } = {},
): Promise<boolean> {
  const store = deps.store ?? (typeof localStorage === 'undefined' ? null : localStorage);
  if (!store) return false;
  const reload = deps.reload ?? (() => window.location.reload());
  const fetchStamp = deps.fetchStamp ?? fetchResetStamp;

  let stamp: string | null;
  try {
    stamp = await fetchStamp(userId);
  } catch {
    return false;
  }
  if (decideReset(stamp, readSeen(store)[userId]) === 'skip') return false;

  /**
   * ⚠️⚠️ **CLEAR FIRST, RECORD SECOND, AND THE ORDER IS THE WHOLE GUARANTEE.**
   * Storage can be blocked outright in this app — enumerating it is what throws
   * — and recording first would leave a browser believing it had already handled
   * a reset it never performed, keeping the stale state for ever. This way a
   * failed clear records nothing and the next boot tries again.
   */
  let dropped: number;
  try {
    dropped = forgetLocalState(store);
  } catch {
    return false;
  }
  rememberSeen(store, userId, stamp as string);

  /* ⚠️ Nothing local to drop — a browser that had never seen this account. It
     gets the stamp recorded and no reload, because reloading an empty cache is
     a flash of the app for no reason. */
  if (dropped === 0) return false;
  reload();
  return true;
}

async function fetchResetStamp(userId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('user_profiles')
    .select('content_reset_at')
    .eq('id', userId)
    .maybeSingle();
  if (error) return null;
  return ((data as { content_reset_at?: string | null } | null)?.content_reset_at) ?? null;
}
